import { IonContent, IonItem, IonLabel, IonModal, IonRadio, IonRadioGroup } from "@ionic/react";
import React, { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet";

// Question interface
interface QuestionData {
    image: string;
    questions: EachQuestion[];
}

interface EachQuestion {
    question: string;
    answer: boolean;
}


const PictureComposition: React.FC = () => {
    const [questions, setQuestions] = useState<QuestionData[]>([]);
    const [selectedAnswers, setSelectedAnswers] = useState<{ [key: number]: boolean | null }>({});
    const [currentIndex, setCurrentIndex] = useState(0);
    const [showImage, setShowImage] = useState(false);
    const [error, setError] = useState(false);

    const modal = useRef<HTMLIonModalElement>(null);

    // Fetch question data
    const fetchQuestions = async () => {
        try {
            let data: QuestionData[] = [];

            if (process.env.REACT_APP_DEVELOPMENT !== 'WEB') {
                const response = await fetch(process.env.REACT_APP_API_URL + 'PictureComposition.json');
                data = await response.json();
            } else {
                const module = await import('../../../datas/PictureComposition.json');
                data = module.default;
            }

            const shuffledQuestions = shuffleArray(data);
            setQuestions(shuffledQuestions);
        } catch (error) {
            console.error('Error loading data:', error);
        }
    };

    useEffect(() => {
        fetchQuestions();
        document.title = "Picture Composition – LKG English Worksheet | Mama’s Guidance"; // Set page title
    }, []);

    const shuffleArray = (array: any[]) => {
        for (let i = array.length - 1; i > 0; i--) {
            const randomIndex = Math.floor(Math.random() * (i + 1));
            [array[i], array[randomIndex]] = [array[randomIndex], array[i]];
        }
        return array;
    };

    const handleOptionClick = (questionIndex: number, selectedValue: boolean) => {
        setSelectedAnswers((prev) => {
            const updatedAnswers = { ...prev, [questionIndex]: selectedValue };
            checkAnswersAndProceed(updatedAnswers);
            return updatedAnswers;
        });
    };

    const checkAnswersAndProceed = (updatedAnswers: { [key: number]: boolean | null }) => {
        const currentQuestionSet = questions[currentIndex]?.questions;
        if (!currentQuestionSet) return;

        // Check if all answers are correct
        const allCorrect = currentQuestionSet.every((q, index) => updatedAnswers[index] === q.answer);
        if (allCorrect) {
            setError(false);
            setShowImage(true);
            setTimeout(() => {
                if (currentIndex + 1 < questions.length) {
                    setCurrentIndex((prev) => prev + 1);
                }
                setShowImage(false);
                setSelectedAnswers({}); // Reset selections
            }, 3000);
        } else {
            setError(true);
        }
    };

    return (
        <>
            <Helmet>
                <meta name="description" content='Enhance creativity and language skills in UKG kids with Picture Composition activities on Mama’s Guidance. Help children express ideas and improve sentence formation through fun, visual-based exercises.' />
            </Helmet>
            {showImage === false ? (
                <div className="seasons additionWord">
                    <div className="showInfo" style={{ width: '80%' }} onClick={() => modal.current?.present()}><i className="fas fa-info-circle"></i></div>
                    <IonModal
                        ref={modal}
                        trigger="open-modal"
                        initialBreakpoint={1}
                        breakpoints={[0, 0.5, 0.75, 1]}
                        handleBehavior="cycle"
                        backdropDismiss={false}  // ✅ Prevents click-to-close
                        className="full-page-modal"
                    >
                        <IonContent className="ion-padding">
                            <div style={{ textAlign: 'right', fontSize: '25px' }} onClick={() => modal.current?.dismiss()}><i className="fas fa-3a fa-times"></i></div>
                            <h1>Picture Composition</h1>

                            <p>
                                Boost your child&apos;s creativity and language skills with this fun and engaging <strong>Picture Composition</strong> worksheet designed especially for LKG learners! Children learn to observe a picture and express their thoughts through simple words or guided sentences.
                            </p>

                            <h2>🖼️ What Is Picture Composition?</h2>
                            <p>
                                Picture composition is a foundational writing activity where children look at an image and describe what they see. It helps build <strong>vocabulary, sentence structure, and creative thinking</strong>. At the LKG level, it focuses more on observation and verbal expression.
                            </p>

                            <h2>🧩 What’s in the Worksheet?</h2>
                            <ul>
                                <li>Bright, child-friendly images like park scenes, fruits, animals, or daily activities</li>
                                <li>Guided questions like: <strong>&apos;What do you see?&apos;</strong> or <strong>&apos;What is the boy doing?&apos;</strong></li>
                                <li>Fill-in-the-blanks based on the image</li>
                                <li>Circle the correct word or choose the matching sentence</li>
                            </ul>

                            <h2>🎯 Learning Objectives</h2>
                            <ul>
                                <li>Improve observation and thinking skills</li>
                                <li>Introduce sentence formation and speaking practice</li>
                                <li>Encourage vocabulary building in a visual context</li>
                                <li>Develop confidence in self-expression</li>
                            </ul>

                            <h2>👩‍🏫 How to Use the Worksheet</h2>
                            <ul>
                                <li>Show your child the picture and ask open-ended questions like <strong>“What do you see?”</strong></li>
                                <li>Encourage them to say simple sentences like <strong>“I see a dog.”</strong> or <strong>“The boy is running.”</strong></li>
                                <li>Let them draw or match words if they&apos;re not yet writing independently.</li>
                                <li>Model answers and help them form complete thoughts.</li>
                            </ul>


                            <h2>🔗 Related Topics</h2>
                            <div>
                                <IonItem routerLink="/lkg/english-language/this-that-these-those"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>This–That & These–Those</IonLabel>
                                </IonItem>
                                <IonItem routerLink="/lkg/english-language/rhyming-words"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>Rhyming Words</IonLabel>
                                </IonItem>
                                <IonItem routerLink="/lkg/english-language/opposites"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>Opposites</IonLabel>
                                </IonItem>
                            </div>

                            <h2>🧠 Why Picture Composition Is Important</h2>
                            <p>
                                Picture-based activities <strong>strengthen language processing</strong> and help children become confident communicators. It&apos;s the first step toward writing and storytelling!
                            </p>

                            <div className="btn hero-btn" onClick={() => modal.current?.dismiss()}>Close</div>
                        </IonContent>
                    </IonModal>
                    <article className="visually-hidden">
                        <h1>Picture Composition</h1>

                        <p>
                            Boost your child&apos;s creativity and language skills with this fun and engaging <strong>Picture Composition</strong> worksheet designed especially for LKG learners! Children learn to observe a picture and express their thoughts through simple words or guided sentences.
                        </p>

                        <h2>🖼️ What Is Picture Composition?</h2>
                        <p>
                            Picture composition is a foundational writing activity where children look at an image and describe what they see. It helps build <strong>vocabulary, sentence structure, and creative thinking</strong>. At the LKG level, it focuses more on observation and verbal expression.
                        </p>

                        <h2>🧩 What’s in the Worksheet?</h2>
                        <ul>
                            <li>Bright, child-friendly images like park scenes, fruits, animals, or daily activities</li>
                            <li>Guided questions like: <strong>&apos;What do you see?&apos;</strong> or <strong>&apos;What is the boy doing?&apos;</strong></li>
                            <li>Fill-in-the-blanks based on the image</li>
                            <li>Circle the correct word or choose the matching sentence</li>
                        </ul>

                        <h2>🎯 Learning Objectives</h2>
                        <ul>
                            <li>Improve observation and thinking skills</li>
                            <li>Introduce sentence formation and speaking practice</li>
                            <li>Encourage vocabulary building in a visual context</li>
                            <li>Develop confidence in self-expression</li>
                        </ul>

                        <h2>👩‍🏫 How to Use the Worksheet</h2>
                        <ul>
                            <li>Show your child the picture and ask open-ended questions like <strong>“What do you see?”</strong></li>
                            <li>Encourage them to say simple sentences like <strong>“I see a dog.”</strong> or <strong>“The boy is running.”</strong></li>
                            <li>Let them draw or match words if they&apos;re not yet writing independently.</li>
                            <li>Model answers and help them form complete thoughts.</li>
                        </ul>


                        <h2>🔗 Related Topics</h2>
                        <div>
                            <IonItem routerLink="/lkg/english-language/this-that-these-those"
                                routerDirection="none"
                                lines="none"
                                detail={true}>
                                <IonLabel>This–That & These–Those</IonLabel>
                            </IonItem>
                            <IonItem routerLink="/lkg/english-language/rhyming-words"
                                routerDirection="none"
                                lines="none"
                                detail={true}>
                                <IonLabel>Rhyming Words</IonLabel>
                            </IonItem>
                            <IonItem routerLink="/lkg/english-language/opposites"
                                routerDirection="none"
                                lines="none"
                                detail={true}>
                                <IonLabel>Opposites</IonLabel>
                            </IonItem>
                        </div>

                        <h2>🧠 Why Picture Composition Is Important</h2>
                        <p>
                            Picture-based activities <strong>strengthen language processing</strong> and help children become confident communicators. It&apos;s the first step toward writing and storytelling!
                        </p>
                    </article>
                    <div className="mt-4 mb-4 half-width">
                        <img className="img-thumbnail" src={process.env.REACT_APP_API_URL + questions[currentIndex]?.image} alt="Right Option" />
                    </div>
                    <div className="mb-5">
                        {questions[currentIndex]?.questions.map((item, index) => (
                            <div key={index}>
                                <h3>{index + 1}. {item.question}</h3>
                                <IonRadioGroup onIonChange={(e) => handleOptionClick(index, e.detail.value!)}>
                                    <IonItem className={`grid-item ${selectedAnswers[index] === true ? (true === item.answer ? "correct" : "errorItems") : ""}`}>
                                        <IonRadio slot="start" value={true} />
                                        <IonLabel>True</IonLabel>
                                    </IonItem>
                                    <IonItem className={`grid-item ${selectedAnswers[index] === false ? (false === item.answer ? "correct" : "errorItems") : ""}`}>
                                        <IonRadio slot="start" value={false} />
                                        <IonLabel>False</IonLabel>
                                    </IonItem>
                                </IonRadioGroup>
                            </div>
                        ))}
                        {error && <p className="error-message">Some answers are incorrect. Try again!</p>}
                    </div>
                </div>
            ) : (
                <div className="contentPart showMiddle">
                    <img style={{ height: '150px' }} className='img-fluid' src={process.env.REACT_APP_WELDONE_LINK} alt="Well Done" />
                </div>
            )}
        </>
    );
};

export default PictureComposition;
